import { Box, Button, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import Page from '../components/Page';
import { AppColors, typographyClamps } from '../utils/utils';

const Finance: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // e.g. <600px

  return (
    <>
      <Page title={'Finance'}>
        <Box
          sx={{
            width: '100%',
            position: 'relative',
            display: 'inline-block',
          }}
        >
          {/* Image */}
          <Box
            component='img'
            src='/static/finance_page/finance_home.png'
            alt='Finance Hero'
            sx={{
              width: '100%',
              height: 'auto',
              maxWidth: '100%',
              display: 'block',
            }}
          />

          {/* Button overlay top-right */}
          <Button
            sx={{
              position: 'absolute',
              top: { xs: 2, sm: 4, md: 14 },
              right: { xs: 2, sm: 8, md: 20 },
              borderRadius: '0 !important',
              backgroundColor: AppColors.primaryColor,
              color: 'white',
              textTransform: 'none',
              padding: { xs: 0, sm: '1px 8px', md: '2px 16px' },
              fontSize: {
                xs: '8px',
                sm: '12px',
                md: typographyClamps.body1,
              },
              whiteSpace: 'pre-line',
              lineHeight: { xs: 1, sm: 1.15, md: 1.35 },
            }}
            onClick={() => {}}
          >
            {'Get Finance\nApplication'}
          </Button>
        </Box>

        <Box
          sx={{
            backgroundColor: 'white',
            color: 'black',
            px: '10%',
            pt: 6,
            borderRadius: 2,
            mt: 4,
          }}
        >
          {/* MAIN TITLE */}
          <Typography
            variant='h3'
            sx={{ fontWeight: 800, mb: 2 }}
          >
            FINANCING WITH STERLING AUTO GROUP
          </Typography>

          {/* SECTION 1 */}
          <Typography
            variant='h6'
            sx={{ fontWeight: 500, mt: 3, mb: 1 }}
          >
            ● Competitive Rates & Terms
          </Typography>

          <Typography sx={{ mb: 1 }}>
            We work with top lenders to secure competitive rates and flexible
            payment plans tailored to your budget.
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 500, mb: 1 }}
          >
            ● Fast & Simple Online Application
          </Typography>
          <Typography sx={{ mb: 1 }}>
            Apply in minutes from your phone—no long paperwork, no hassle.
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 500, mb: 1 }}
          >
            ● Personalized Payment Options
          </Typography>
          <Typography sx={{ mb: 1 }}>
            Our finance specialists tailor monthly payment options based on your
            goals and driving habits.
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 500, mb: 1 }}
          >
            ● All Credit Situations Welcome
          </Typography>
          <Typography sx={{ mb: 1 }}>
            Whether you're rebuilding credit or seeking premium financing, we
            help you get approved smoothly.
          </Typography>

          {/* SECTION 2 – SPECIALS */}
          <Typography
            variant='h3'
            sx={{ fontWeight: 800, mt: 5, mb: 2 }}
          >
            STERLING FINANCE SPECIALS
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 500, mb: 1 }}
          >
            ● Seasonal APR Offers
          </Typography>
          <Typography sx={{ mb: 1 }}>
            Enjoy exclusive low APR opportunities on select new vehicles.
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 500, mb: 1 }}
          >
            ● Lease Specials
          </Typography>
          <Typography sx={{ mb: 1 }}>
            Drive the latest models with low monthly lease payments and flexible
            terms.
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 500, mb: 1 }}
          >
            ● Certified Pre-Owned Incentives
          </Typography>
          <Typography sx={{ mb: 1 }}>
            Extra savings, complimentary warranties, and preferred financing on
            eligible CPO models.
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 500, mb: 1 }}
          >
            ● Military, First Responder & Loyalty Programs
          </Typography>
          <Typography sx={{ mb: 1 }}>
            Special appreciation incentives for qualifying customers.
          </Typography>

          {/* SECTION 3 – WHY FINANCE */}
          <Typography
            variant='h3'
            sx={{ fontWeight: 800, mt: 5, mb: 2 }}
          >
            WHY FINANCE WITH STERLING
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 500, mb: 1 }}
          >
            ● A Premium Experience from Start to Finish
          </Typography>
          <Typography sx={{ mb: 1 }}>
            Enjoy a smooth financing process designed around honesty, comfort,
            and your unique needs.
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 500, mb: 1 }}
          >
            ● Transparent, Up-Front Numbers
          </Typography>
          <Typography sx={{ mb: 1 }}>
            No hidden fees—ever. Just clear, honest pricing.
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 500, mb: 1 }}
          >
            ● One-on-One Finance Guidance
          </Typography>
          <Typography sx={{ mb: 1 }}>
            Our specialists walk you through every option so you can choose with
            confidence.
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 500, mb: 1 }}
          >
            ● Wide Lender Network
          </Typography>
          <Typography sx={{ mb: 1 }}>
            More approvals. More competitive rates. More choices.
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 500, mb: 1 }}
          >
            ● Trusted by Local Drivers
          </Typography>
          <Typography sx={{ mb: 1 }}>
            A reputation built on integrity, customer care, and long-term
            relationships.
          </Typography>

          {/* SECTION 4 – SERVICE */}
          <Typography
            variant='h3'
            sx={{ fontWeight: 800, mt: 5, mb: 2 }}
          >
            SERVICE AT STERLING
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 500, mb: 1 }}
          >
            ● Precision Care for Every Mile Ahead
          </Typography>
          <Typography sx={{ mb: 1 }}>
            Our factory-trained technicians ensure expert diagnostics and
            professional-grade repair quality.
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 500, mb: 1 }}
          >
            ● Genuine Parts & Components
          </Typography>
          <Typography sx={{ mb: 1 }}>
            We use OEM-level parts to maintain performance, reliability, and
            longevity.
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 500, mb: 1 }}
          >
            ● Online Scheduling
          </Typography>
          <Typography sx={{ mb: 1 }}>
            Choose your service time instantly through our online scheduling
            system.
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 500, mb: 1 }}
          >
            ● Courtesy Vehicles Available
          </Typography>
          <Typography sx={{ mb: 1 }}>
            Stay on the move with complimentary loaner vehicles for qualifying
            services.
          </Typography>
        </Box>

        <Grid
          container
          display={'flex'}
          justifyContent={'center'}
          my={5}
        >
          <Button
            sx={{
              borderRadius: '0 !important',
              backgroundColor: AppColors.primaryColor,
              color: 'white',
              textTransform: 'none',
              padding: '2px 26px',
              fontSize: {
                // xs: '8px',
                sm: '12px',
                md: typographyClamps.body1,
              },
              whiteSpace: 'pre-line',
              lineHeight: { xs: 1, sm: 1.15, md: 1.35 },
            }}
            onClick={() => {}}
          >
            {'Get Finance\nApplication'}
          </Button>
        </Grid>

        {!isMobile ? <Box
          component={'img'}
          src='/static/finance_page/finance_offers.png'
          sx={{
            width: '100%',
          }}
        /> : (
        <Box>
          {['finance_offer_1.jpg', 'finance_offer_2.jpg', 'finance_offer_3.jpg'].map((item)=>  <Box
          component={'img'}
          src={`/static/finance_page/${item}`}
          sx={{
            width: '100%',
          }}/>)}
        </Box>)}

        {isMobile && <Box
          component={'img'}
          src='/static/finance_page/offer_footer_mobile.jpg'
          sx={{
            width: '100%',
          }}
        />}
      </Page>
    </>
  );
};

export default Finance;
